import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPaymentAdmin, listRefunds, type Payment, type Refund } from "@/lib/api";
import { formatAmount, formatDate, formatRelative } from "@/lib/format";
import { ArrowLeft, RotateCcw, Smartphone, CreditCard, User, Hash, Calendar } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Badge, statusColor } from "@/components/DataTable";
import { RefundModal } from "@/components/RefundModal";
import { useAuth } from "@/contexts/AuthContext";

type FullPayment = Payment & {
  livemode?: boolean;
  switch_request_id?: string | null;
  switch_response?: Record<string, unknown> | string | null;
  amount_refunded?: number;
  callback_url?: string | null;
};

export function PaymentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const userRole = auth.status === "authenticated" ? auth.user.role : null;
  // IT staff has read-only access on payments — cannot initiate refunds.
  const canRefundByRole = userRole !== "it" && userRole !== "commercial";
  const [payment, setPayment] = useState<FullPayment | null>(null);
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [p, r] = await Promise.all([
        getPaymentAdmin(id) as Promise<FullPayment>,
        listRefunds(id),
      ]);
      setPayment(p);
      setRefunds(r.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <div className="card card-body text-[#7e8299]">Chargement…</div>;
  if (error || !payment) return <div className="card card-body text-danger-700">{error ?? "Paiement introuvable"}</div>;

  const alreadyRefunded = refunds
    .filter((r) => r.status === "succeeded" || r.status === "pending" || r.status === "processing")
    .reduce((acc, r) => acc + r.amount_minor, 0);
  const remaining = payment.amount_minor - alreadyRefunded;
  const canRefund = canRefundByRole && payment.status === "succeeded" && remaining > 0;
  const isMobileMoney = ["mpesa", "orange", "airtel", "afrimoney"].includes(payment.payment_method);

  return (
    <div>
      <PageHeader
        title={
          <div className="flex items-center gap-3 flex-wrap">
            <span>Paiement</span>
            <Badge color={statusColor(payment.status)}>{payment.status}</Badge>
          </div>
        }
        breadcrumbs={[
          { label: "Plateforme" },
          { label: "Paiements", to: "/payments" },
          { label: payment.id.slice(0, 14) + "…" },
        ]}
        actions={
          <>
            <button onClick={() => navigate(-1)} className="btn btn-light btn-sm">
              <ArrowLeft size={14} />
              Retour
            </button>
            {canRefund && (
              <button onClick={() => setRefundOpen(true)} className="btn btn-danger btn-sm">
                <RotateCcw size={14} />
                Rembourser
              </button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main info card */}
        <div className="card lg:col-span-2">
          <div className="card-header">
            <div className="card-title">Informations</div>
          </div>
          <div className="card-body">
            <div className="flex items-baseline gap-2 mb-6">
              <div className="text-4xl font-bold text-[#181c32]">{formatAmount(payment.amount_minor, payment.currency)}</div>
              {alreadyRefunded > 0 && (
                <div className="text-sm text-danger-700">
                  − {formatAmount(alreadyRefunded, payment.currency)} remboursé
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field icon={<Hash size={14} />} label="ID" value={payment.id} mono />
              <Field icon={<Hash size={14} />} label="Référence" value={payment.external_reference} mono />
              <Field
                icon={isMobileMoney ? <Smartphone size={14} /> : <CreditCard size={14} />}
                label="Méthode"
                value={payment.payment_method}
              />
              <Field icon={<Calendar size={14} />} label="Créé le" value={formatDate(payment.created_at)} />
              {payment.completed_at && (
                <Field icon={<Calendar size={14} />} label="Complété le" value={formatDate(payment.completed_at)} />
              )}
              <Field icon={<User size={14} />} label="Marchand" value={payment.merchant_id} mono />
            </div>

            {payment.failure_message && (
              <div
                className={`mt-6 rounded-lg border-l-4 px-4 py-3 ${
                  payment.status === "succeeded"
                    ? "bg-success-50 border-success-500"
                    : "bg-danger-50 border-danger-500"
                }`}
              >
                <div
                  className={`font-bold text-sm mb-1 ${
                    payment.status === "succeeded" ? "text-success-700" : "text-danger-700"
                  }`}
                >
                  {payment.status === "succeeded" ? "Message du switch" : "Échec du paiement"}
                </div>
                <div
                  className={`text-xs ${
                    payment.status === "succeeded" ? "text-success-700" : "text-danger-700"
                  }`}
                >
                  {payment.failure_message}
                </div>
                {payment.failure_code && (
                  <div
                    className={`text-xs mt-1 ${
                      payment.status === "succeeded" ? "text-success-700" : "text-danger-700"
                    }`}
                  >
                    Code: <span className="font-mono">{payment.failure_code}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Customer card */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Client</div>
          </div>
          <div className="card-body">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center font-bold text-base">
                {[payment.customer_first_name, payment.customer_last_name]
                  .filter(Boolean)
                  .map((s) => s![0]?.toUpperCase() ?? "")
                  .join("") || "?"}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-[#181c32] truncate">
                  {[payment.customer_first_name, payment.customer_last_name].filter(Boolean).join(" ") || "—"}
                </div>
                <div className="text-xs text-[#7e8299] truncate">{payment.customer_email ?? "Pas d'email"}</div>
              </div>
            </div>
            <div className="space-y-3">
              <Field label="Téléphone" value={payment.customer_phone ?? "—"} mono />
              <Field label="Email" value={payment.customer_email ?? "—"} />
            </div>
          </div>
        </div>

        {/* Refunds list */}
        <div className="card lg:col-span-3">
          <div className="card-header">
            <div>
              <div className="card-title">Remboursements</div>
              <div className="card-subtitle">
                {refunds.length === 0
                  ? "Aucun remboursement"
                  : `${refunds.length} remboursement${refunds.length > 1 ? "s" : ""} · Total ${formatAmount(alreadyRefunded, payment.currency)}`}
              </div>
            </div>
            {canRefund && (
              <button onClick={() => setRefundOpen(true)} className="btn btn-light-danger btn-sm">
                <RotateCcw size={14} />
                Nouveau remboursement
              </button>
            )}
          </div>
          {refunds.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#f9fafb]">
                  <tr className="text-left">
                    <th className="px-6 py-3 text-xs uppercase tracking-wider font-bold text-[#3f4254]">ID</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold text-[#3f4254] text-right">Montant</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold text-[#3f4254]">Status</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold text-[#3f4254]">Motif</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold text-[#3f4254]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {refunds.map((r) => (
                    <tr key={r.id} className="border-t border-[#eff2f5]">
                      <td className="px-6 py-3 font-mono text-xs text-[#7e8299]">{r.id}</td>
                      <td className="px-4 py-3 text-right font-bold">{formatAmount(r.amount_minor, r.currency)}</td>
                      <td className="px-4 py-3">
                        <Badge color={statusColor(r.status)}>{r.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#3f4254] max-w-xs truncate">{r.reason ?? "—"}</td>
                      <td className="px-4 py-3 text-xs text-[#7e8299]" title={formatDate(r.created_at)}>
                        {formatRelative(r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-10 text-center text-sm text-[#7e8299]">
              {payment.status === "succeeded"
                ? "Aucun remboursement n'a été effectué sur ce paiement."
                : `Les remboursements sont possibles uniquement sur les paiements en statut "succeeded".`}
            </div>
          )}
        </div>
      </div>

      <RefundModal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        payment={{
          id: payment.id,
          amount_minor: payment.amount_minor,
          currency: payment.currency,
          livemode: payment.livemode,
          external_reference: payment.external_reference,
        }}
        alreadyRefunded={alreadyRefunded}
        onSuccess={() => load()}
      />
    </div>
  );
}

function Field({
  icon,
  label,
  value,
  mono,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold mb-1.5 inline-flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={`text-sm text-[#181c32] ${mono ? "font-mono break-all" : ""}`}>{value}</div>
    </div>
  );
}
